import React, { useCallback, useState } from 'react';
import { Alert, Platform } from 'react-native';

import {
  type HeroVideoCacheSnapshot,
  clearHeroVideoCache,
  inspectHeroVideoCache,
} from '@fhs/home/src/native-homepage-hero/hero-video-cache';
import { Box, Button, HStack } from '@fhs-legacy/universal-components';
import { EnvItem, EnvListContainer, HiddenSection } from 'pages/diagnostics/components';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const HeroVideoCacheContent = () => {
  const [snapshot, setSnapshot] = useState<HeroVideoCacheSnapshot>(() => inspectHeroVideoCache());

  const refresh = useCallback(() => {
    setSnapshot(inspectHeroVideoCache());
  }, []);

  const onClear = useCallback(() => {
    const ok = clearHeroVideoCache();
    setSnapshot(inspectHeroVideoCache());
    // Two paragraphs deliberately: (1) tells them if it worked, (2) explains
    // why they still see LTO in the current session and what to do to
    // exercise the cold path.
    Alert.alert(
      ok ? 'Hero video cache cleared' : 'Failed to clear hero video cache',
      ok
        ? 'The cache directory has been deleted. Force-quit and relaunch the app to test the cold path (LTO will fetch from network again and re-seed the cache).'
        : 'The directory may have already been evicted by the OS. Try again after relaunching the app.'
    );
  }, []);

  // Non-iOS is a no-op — Android relies on expo-video's built-in caching, and
  // web has no video cache at all. Surfacing this in the UI keeps QA from
  // filing false bugs when the section shows empty on an Android device.
  if (Platform.OS !== 'ios') {
    return <Box>{`Hero video disk cache is iOS-only (current platform: ${Platform.OS}).`}</Box>;
  }

  const totalBytes = snapshot.files.reduce((acc, entry) => acc + entry.sizeBytes, 0);

  return (
    <>
      <HStack space="$2">
        <Button onPress={onClear} size="sm">
          Clear cache
        </Button>
        <Button onPress={refresh} size="sm" variant="ghost">
          Refresh
        </Button>
      </HStack>
      <EnvListContainer>
        <EnvItem envKey="Cache directory" value={snapshot.dirExists ? 'exists' : 'empty'} />
        <EnvItem envKey="Cached files" value={String(snapshot.files.length)} />
        <EnvItem envKey="Total size" value={formatBytes(totalBytes)} />
        {snapshot.files.map(entry => (
          <EnvItem key={entry.name} envKey={entry.name} value={formatBytes(entry.sizeBytes)} />
        ))}
      </EnvListContainer>
    </>
  );
};

export const HeroVideoCache = () => (
  <HiddenSection title="Hero Video Cache" content={<HeroVideoCacheContent />} />
);
